import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeft, ChevronRight, RotateCcw, Home, CheckCircle2, Sparkles, AlertCircle } from 'lucide-react';
import { AnalysisData, StepType, StepState, UnitContext, StepRecord, ComprehensiveEvaluation } from '../types';
import Step1Process from '../components/steps/Step1Process';
import Step2Participant from '../components/steps/Step2Participant';
import Step3Connection from '../components/steps/Step3Connection';
import Step4Clause from '../components/steps/Step4Clause';
import Step5Translation from '../components/steps/Step5Translation';
import StepIndicator from '../components/StepIndicator';
import StepHelpPopover from '../components/StepHelpPopover';
import PracticeSummary from '../components/PracticeSummary';
import { isAIConfigured, getComprehensiveEvaluation } from '../services/ai';
import { analyzeText } from '../services/api';
import { 
  startPractice, 
  saveStepRecord, 
  completePractice, 
  getNextSentence 
} from '../services/practice';

interface PracticePageProps {
  analysisData: AnalysisData | null;
  unitContext: UnitContext | null;
  onUpdateUnitContext: (context: UnitContext | null) => void;
  onClearContext: () => void;
}

const stepTitles: Record<StepType, { title: string; subtitle: string }> = {
  1: { title: '过程识别', subtitle: '识别句中的过程（动词及相关成分）' },
  2: { title: '对象识别', subtitle: '识别句中的对象（参与者/名词词组）' },
  3: { title: '建立连接', subtitle: '将过程与其管辖的对象建立关系' },
  4: { title: '句子拆解', subtitle: '将长句按层级拆解为小句' },
  5: { title: '逐层翻译', subtitle: '从外层到内层逐步翻译' },
};

const stepThemeColors: Record<StepType, 'cyan' | 'blue' | 'purple' | 'yellow' | 'pink'> = {
  1: 'cyan',
  2: 'blue',
  3: 'purple',
  4: 'yellow',
  5: 'pink',
};

export default function PracticePage({ 
  analysisData, 
  unitContext,
  onUpdateUnitContext,
  onClearContext
}: PracticePageProps) {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState<StepType>(1);
  const [resetKeys, setResetKeys] = useState<Record<StepType, number>>({
    1: 0, 2: 0, 3: 0, 4: 0, 5: 0
  });
  const [stepStates, setStepStates] = useState<StepState[]>([
    { step: 1, completed: false, attempts: 0 },
    { step: 2, completed: false, attempts: 0 },
    { step: 3, completed: false, attempts: 0 },
    { step: 4, completed: false, attempts: 0 },
    { step: 5, completed: false, attempts: 0 },
  ]);
  const [aiConfigured, setAiConfigured] = useState(false);
  
  // 练习记录相关状态
  const [practiceId, setPracticeId] = useState<string | null>(null);
  const [startedAt, setStartedAt] = useState<string>('');
  const [completedAt, setCompletedAt] = useState<string>('');
  const [stepRecords, setStepRecords] = useState<StepRecord[]>([]);
  const [showSummary, setShowSummary] = useState(false);
  const [hasNextSentence, setHasNextSentence] = useState(false);
  const [_loadingNext, setLoadingNext] = useState(false);
  const [comprehensiveEvaluation, setComprehensiveEvaluation] = useState<ComprehensiveEvaluation | null>(null);
  const [loadingEvaluation, setLoadingEvaluation] = useState(false);
  
  // 每步开始时间记录
  const stepStartTimeRef = useRef<number>(Date.now());
  
  // 当前分析数据（支持连续练习时更新）
  const [currentAnalysisData, setCurrentAnalysisData] = useState<AnalysisData | null>(analysisData);

  useEffect(() => {
    if (!analysisData) {
      navigate('/');
    } else {
      setCurrentAnalysisData(analysisData);
    }
  }, [analysisData, navigate]);

  // 初始化练习记录
  useEffect(() => {
    if (currentAnalysisData && !practiceId) {
      initPractice();
    }
  }, [currentAnalysisData]);

  // Check AI configuration status
  useEffect(() => {
    isAIConfigured().then(setAiConfigured).catch(() => setAiConfigured(false));
  }, []);

  // 检查是否有下一句
  useEffect(() => {
    if (unitContext) {
      checkNextSentence();
    }
  }, [unitContext]);

  const initPractice = async () => {
    if (!currentAnalysisData) return;
    
    try {
      const sentenceText = currentAnalysisData.sentences[0]?.text || '';
      const result = await startPractice({
        sentence_id: unitContext?.currentSentenceId,
        unit_id: unitContext?.unitId,
        sentence_text: sentenceText,
      });
      setPracticeId(result.practice_id);
      setStartedAt(result.started_at);
      stepStartTimeRef.current = Date.now();
      
      // 保存到 localStorage
      localStorage.setItem('current_practice_id', result.practice_id);
    } catch (err) {
      console.error('Failed to start practice:', err);
    }
  };

  const checkNextSentence = async () => {
    if (!unitContext) return;
    
    try {
      await getNextSentence(unitContext.unitId, unitContext.currentSentenceId);
      setHasNextSentence(true);
    } catch {
      setHasNextSentence(false);
    }
  };

  if (!currentAnalysisData) {
    return null;
  }

  const handleStepComplete = async (step: StepType, score: number, aiFeedback?: string) => {
    const duration = Math.floor((Date.now() - stepStartTimeRef.current) / 1000);
    const attempts = (stepStates.find(s => s.step === step)?.attempts || 0) + 1;
    
    // 更新本地状态
    setStepStates(prev => prev.map(s => 
      s.step === step 
        ? { ...s, completed: true, score, attempts }
        : s
    ));

    // 保存到后端
    if (practiceId) {
      try {
        await saveStepRecord(practiceId, {
          step_num: step,
          score,
          attempts,
          duration_seconds: duration,
          ai_feedback: aiFeedback,
        });

        // 更新本地步骤记录
        const newRecord: StepRecord = {
          id: `step-${step}`,
          step_num: step,
          score,
          attempts,
          duration_seconds: duration,
          ai_feedback: aiFeedback || null,
          completed_at: new Date().toISOString(),
        };
        setStepRecords(prev => {
          const existing = prev.findIndex(r => r.step_num === step);
          if (existing >= 0) {
            const updated = [...prev];
            updated[existing] = newRecord;
            return updated;
          }
          return [...prev, newRecord];
        });
      } catch (err) {
        console.error('Failed to save step record:', err);
      }
    }

    // 重置下一步的开始时间
    stepStartTimeRef.current = Date.now();

    // 检查是否全部完成
    if (step === 5) {
      await handlePracticeComplete();
    }
  };

  const handlePracticeComplete = async () => {
    // 计算总分（各步平均分）
    const allSteps = stepStates.map((s, i) => 
      i === 4 ? { ...s, completed: true } : s
    );
    const completedSteps = allSteps.filter(s => s.completed && s.score !== undefined);
    const totalScore = completedSteps.length > 0
      ? completedSteps.reduce((sum, s) => sum + (s.score || 0), 0) / completedSteps.length
      : 0;

    if (practiceId) {
      try {
        const result = await completePractice(practiceId, totalScore);
        setCompletedAt(result.completed_at);
      } catch (err) {
        console.error('Failed to complete practice:', err);
      }
    }

    // 获取 AI 综合评价
    if (aiConfigured && currentAnalysisData) {
      setLoadingEvaluation(true);
      try {
        // 获取翻译环节的 AI 评价
        const translationStep = stepRecords.find(s => s.step_num === 5);
        const translationFeedback = translationStep?.ai_feedback || undefined;
        
        const evaluation = await getComprehensiveEvaluation(
          currentAnalysisData.sentences[0]?.text || '',
          stepRecords,
          translationFeedback
        );
        setComprehensiveEvaluation(evaluation);
      } catch (err) {
        console.error('Failed to get comprehensive evaluation:', err);
      } finally {
        setLoadingEvaluation(false);
      }
    }

    // 显示总结页面
    setShowSummary(true);
  };

  const handleNextStep = () => {
    if (currentStep < 5) {
      stepStartTimeRef.current = Date.now();
      setCurrentStep((currentStep + 1) as StepType);
    }
  };

  const handlePrevStep = () => {
    if (currentStep > 1) {
      setCurrentStep((currentStep - 1) as StepType);
    }
  };

  const handleReset = () => {
    setStepStates(prev => prev.map(s => 
      s.step === currentStep 
        ? { ...s, completed: false, score: undefined }
        : s
    ));
    setResetKeys(prev => ({
      ...prev,
      [currentStep]: prev[currentStep] + 1
    }));
    stepStartTimeRef.current = Date.now();
  };

  const handleNextSentence = async () => {
    if (!unitContext) return;
    
    setLoadingNext(true);
    try {
      const nextInfo = await getNextSentence(unitContext.unitId, unitContext.currentSentenceId);
      
      // 分析新句子
      const newAnalysis = await analyzeText({ text: nextInfo.text });
      
      // 更新单元上下文
      onUpdateUnitContext({
        ...unitContext,
        currentSentenceId: nextInfo.id,
        currentSentenceOrder: nextInfo.order_num,
      });

      // 重置状态
      setCurrentAnalysisData(newAnalysis);
      setCurrentStep(1);
      setStepStates([
        { step: 1, completed: false, attempts: 0 },
        { step: 2, completed: false, attempts: 0 },
        { step: 3, completed: false, attempts: 0 },
        { step: 4, completed: false, attempts: 0 },
        { step: 5, completed: false, attempts: 0 },
      ]);
      setResetKeys({ 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 });
      setPracticeId(null);
      setStepRecords([]);
      setShowSummary(false);
      setHasNextSentence(nextInfo.has_next);
      
    } catch (err) {
      console.error('Failed to load next sentence:', err);
    } finally {
      setLoadingNext(false);
    }
  };

  const handleRetry = () => {
    // 重置所有状态，重新开始当前句子
    setCurrentStep(1);
    setStepStates([
      { step: 1, completed: false, attempts: 0 },
      { step: 2, completed: false, attempts: 0 },
      { step: 3, completed: false, attempts: 0 },
      { step: 4, completed: false, attempts: 0 },
      { step: 5, completed: false, attempts: 0 },
    ]);
    setResetKeys({ 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 });
    setPracticeId(null);
    setStepRecords([]);
    setShowSummary(false);
    stepStartTimeRef.current = Date.now();
  };

  const handleGoHome = () => {
    onClearContext();
    localStorage.removeItem('current_practice_id');
    navigate('/');
  };

  // 显示总结页面
  if (showSummary) {
    const totalScore = stepStates.filter(s => s.completed && s.score !== undefined)
      .reduce((sum, s) => sum + (s.score || 0), 0) / 5;

    return (
      <div className="max-w-6xl mx-auto px-6 py-8">
        <PracticeSummary
          sentenceText={currentAnalysisData.sentences[0]?.text || ''}
          steps={stepRecords}
          totalScore={totalScore}
          startedAt={startedAt}
          completedAt={completedAt}
          unitContext={unitContext}
          hasNextSentence={hasNextSentence}
          onNextSentence={handleNextSentence}
          onRetry={handleRetry}
          onGoHome={handleGoHome}
          comprehensiveEvaluation={comprehensiveEvaluation}
          loadingEvaluation={loadingEvaluation}
        />
      </div>
    );
  }

  const currentStepState = stepStates.find(s => s.step === currentStep)!;
  const { title, subtitle } = stepTitles[currentStep];
  const themeColor = stepThemeColors[currentStep];

  const step1Completed = stepStates.find(s => s.step === 1)?.completed || false;
  const step2Completed = stepStates.find(s => s.step === 2)?.completed || false;

  const renderStepHelp = () => {
    switch (currentStep) {
      case 1:
        return (
          <>
            <p className="font-semibold text-cyan-300 mb-2">过程（Process）识别</p>
            <p>请<span className="text-cyan-300">逐个点击</span>选择句中所有的过程成分，包括：</p>
            <ul className="mt-2 space-y-1 text-gray-500">
              <li>• 主要动词（如 suggest, develop）</li>
              <li>• 助动词+动词（如 is raised 中的 is 和 raised 都要选）</li>
            </ul>
            <p className="mt-2 text-yellow-400/80">
              提示：注意选择完整的过程，包括助动词
            </p>
          </>
        );
      case 2:
        return (
          <>
            <p className="font-semibold text-blue-300 mb-2">参与者（Participant）识别</p>
            <p>请<span className="text-blue-300">逐个点击</span>选择句中所有的参与者成分：</p>
            <ul className="mt-2 space-y-1 text-gray-500">
              <li>• 主语（执行动作的人或事物）</li>
              <li>• 宾语（接受动作的人或事物）</li>
            </ul>
            {step1Completed && (
              <p className="mt-2 text-cyan-400/80">
                提示：<span className="text-red-400">红色高亮</span> 的是已识别的过程（不可点击）
              </p>
            )}
          </>
        );
      case 3:
        return (
          <>
            <p className="font-semibold text-purple-300 mb-2">建立连接</p>
            <p>用弧线连接<span className="text-red-400">过程</span>与参与者，表示主谓宾关系：</p>
            <ul className="mt-2 space-y-1 text-gray-500">
              <li>• 从<span className="text-blue-400">蓝色主语</span>拖拽到<span className="text-red-400">红色过程</span> → 主语→过程</li>
              <li>• 从<span className="text-red-400">红色过程</span>拖拽到<span className="text-green-400">绿色宾语</span> → 过程→宾语</li>
            </ul>
            <p className="mt-2 text-yellow-400/80">
              注意：过程和参与者都以完整短语的形式显示
            </p>
          </>
        );
      case 4:
        return (
          <>
            <p className="font-semibold text-yellow-300 mb-2">句子拆解</p>
            <p>为每个小句片段设置正确的<span className="text-yellow-300">层级缩进</span>：</p>
            <ul className="mt-2 space-y-1 text-gray-500">
              <li>• 使用 <code className="text-cyan-300">◀ ▶</code> 按钮调整层级</li>
              <li>• Level 0：主句</li>
              <li>• Level 1+：从句/嵌套结构（缩进越深层级越高）</li>
            </ul>
          </>
        );
      case 5:
        return (
          <>
            <p className="font-semibold text-pink-300 mb-2">逐句翻译</p>
            <p>翻译每个<span className="text-pink-300">小句片段</span>，顺序不限。</p>
            <ul className="mt-2 space-y-1 text-gray-500">
              <li>• 顶部刻度线显示<span className="text-white">层级</span>，虚线为对齐参考</li>
              <li>• 在每个小句下方输入对应翻译</li>
              <li>• 所有片段完成后，点击"统一评估"获取评分</li>
            </ul>
            {aiConfigured ? (
              <p className="mt-2 text-green-400/80 flex items-center gap-1">
                <Sparkles className="w-4 h-4" />
                AI 评估已启用 - 完成所有翻译后统一评分
              </p>
            ) : (
              <p className="mt-2 text-yellow-400/80 flex items-center gap-1">
                <AlertCircle className="w-4 h-4" />
                AI 评估未配置，请在 .env 文件中设置 VITE_DEEPSEEK_API_KEY
              </p>
            )}
          </>
        );
      default:
        return null;
    }
  };

  const sentenceTokens = currentAnalysisData.tokens.filter(t => !t.text.match(/^\s+$/));

  const renderStepContent = () => {
    const baseProps = {
      tokens: sentenceTokens,
      clauses: currentAnalysisData.clauses,
      nounChunks: currentAnalysisData.noun_chunks,
      sentences: currentAnalysisData.sentences,
      onComplete: (score: number, aiFeedback?: string) => handleStepComplete(currentStep, score, aiFeedback),
      isCompleted: currentStepState.completed,
    };

    switch (currentStep) {
      case 1:
        return <Step1Process key={`step1-${resetKeys[1]}`} {...baseProps} />;
      case 2:
        return <Step2Participant key={`step2-${resetKeys[2]}`} {...baseProps} showProcessHint={step1Completed} />;
      case 3:
        return <Step3Connection key={`step3-${resetKeys[3]}`} {...baseProps} showProcessHint={step1Completed} showParticipantHint={step2Completed} />;
      case 4:
        return <Step4Clause key={`step4-${resetKeys[4]}`} {...baseProps} showProcessHint={step1Completed} showParticipantHint={step2Completed} />;
      case 5:
        return <Step5Translation key={`step5-${resetKeys[5]}`} {...baseProps} showProcessHint={step1Completed} showParticipantHint={step2Completed} aiConfigured={aiConfigured} />;
      default:
        return null;
    }
  };

  return (
    <div className="max-w-6xl mx-auto px-6 py-8">
      {/* 顶部导航 */}
      <div className="flex items-center justify-between mb-8">
        <motion.button
          whileHover={{ x: -3 }}
          onClick={handleGoHome}
          className="flex items-center gap-2 transition-colors"
          style={{ color: 'var(--theme-text-secondary)' }}
        >
          <Home className="w-5 h-5" />
          <span className="font-body">返回首页</span>
        </motion.button>

        {/* 单元信息 */}
        {unitContext && (
          <div className="text-sm" style={{ color: 'var(--theme-text-muted)' }}>
            {unitContext.courseName} · {unitContext.unitName} · 第 {unitContext.currentSentenceOrder} 句
          </div>
        )}

        {/* 步骤指示器 */}
        <StepIndicator 
          currentStep={currentStep} 
          stepStates={stepStates}
          onStepClick={setCurrentStep}
        />
      </div>

      {/* 原句显示 */}
      <motion.div
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        className="cyber-card p-6 mb-8"
      >
        <div className="flex items-center gap-2 mb-3">
          <div className="w-1.5 h-1.5 rounded-full" style={{ background: 'var(--theme-accent)' }} />
          <span className="text-xs font-body uppercase tracking-wider" style={{ color: 'var(--theme-text-muted)' }}>原句</span>
        </div>
        <p className="text-lg font-mono leading-relaxed" style={{ color: 'var(--theme-text-primary)' }}>
          {currentAnalysisData.sentences[0]?.text || ''}
        </p>
      </motion.div>

      {/* 当前步骤标题 */}
      <motion.div
        key={currentStep}
        initial={{ opacity: 0, x: 20 }}
        animate={{ opacity: 1, x: 0 }}
        exit={{ opacity: 0, x: -20 }}
        className="mb-6"
      >
        <div className="flex items-center gap-4">
          <div className="step-indicator active text-lg">
            {currentStep}
          </div>
          <div className="flex items-center gap-2">
            <div>
              <h2 className="font-display text-2xl" style={{ color: 'var(--theme-accent)' }}>
                {title}
              </h2>
              <p className="font-body" style={{ color: 'var(--theme-text-muted)' }}>{subtitle}</p>
            </div>
            <StepHelpPopover stepType={currentStep} themeColor={themeColor}>
              {renderStepHelp()}
            </StepHelpPopover>
          </div>
          
          {currentStepState.completed && (
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              className="flex items-center gap-2 ml-auto px-4 py-2 rounded-full bg-green-500/20 border border-green-500/40"
            >
              <CheckCircle2 className="w-5 h-5 text-green-400" />
              <span className="text-green-400 font-display">
                {currentStepState.score !== undefined 
                  ? `${Math.round(currentStepState.score * 100)}%` 
                  : '已完成'}
              </span>
            </motion.div>
          )}
        </div>
      </motion.div>

      {/* 练习区域 */}
      <motion.div
        className="cyber-card p-8 min-h-[400px] mb-8"
        layout
      >
        <AnimatePresence mode="wait">
          <motion.div
            key={currentStep}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3 }}
          >
            {renderStepContent()}
          </motion.div>
        </AnimatePresence>
      </motion.div>

      {/* 底部控制栏 */}
      <div className="flex items-center justify-between">
        <motion.button
          whileHover={{ x: -3 }}
          whileTap={{ scale: 0.98 }}
          onClick={handlePrevStep}
          disabled={currentStep === 1}
          className="cyber-button flex items-center gap-2 disabled:opacity-30 disabled:cursor-not-allowed"
        >
          <ChevronLeft className="w-5 h-5" />
          <span className="font-display">上一步</span>
        </motion.button>

        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={handleReset}
          className="cyber-button flex items-center gap-2"
        >
          <RotateCcw className="w-4 h-4" />
          <span className="font-display">重做本步</span>
        </motion.button>

        <div className="flex flex-col items-end gap-1">
          <motion.button
            whileHover={{ x: 3 }}
            whileTap={{ scale: 0.98 }}
            onClick={handleNextStep}
            disabled={currentStep === 5 || !currentStepState.completed}
            className="cyber-button-primary flex items-center gap-2 disabled:opacity-30 disabled:cursor-not-allowed"
          >
            <span className="font-display">下一步</span>
            <ChevronRight className="w-5 h-5" />
          </motion.button>
          {!currentStepState.completed && currentStep < 5 && (
            <span className="text-xs text-yellow-400/70">请先验证答案</span>
          )}
        </div>
      </div>
    </div>
  );
}
